// packages/engine/src/process-trace-timing.ts
//
// P4 — the per-branch virtual-clock race resolver + the test-pin injection map.
// `foldParallel` (process-latency.ts) does order-statistics over branch intervals
// and DISCARDS which branch produced a value, so it cannot name a race winner or
// route to its continuation (design §10). This module supplies that:
//
//   1. buildInjectionMap — resolve each `after`/`takes` pin to the IR call/await
//      node it targets, producing the {@link Injected} map the latency fold reads
//      (design §9 precedence: injected → declared → OPEN). `after` pins the
//      BUSINESS axis, `takes` the SYSTEM axis.
//
//   2. resolveRaces — for each `race` / `race N` / `join N` / `parallel await …
//      else` container, fold every branch under the injection map to a cumulative
//      **virtual wall-clock** (system + business summed onto ONE timeline —
//      design §8; the "never summed" rule is a reporting/budget rule, not an
//      ordering rule) and pick the winner. A clean separation ⇒ a single
//      deterministic outcome (dropped from the enumerator's OPEN list); an
//      overlap / an OPEN clock ⇒ every plausible-winner outcome, so the modal
//      evaluator sees both in T (`sometimes` proves, `happens` is OPEN).
//
// The outcomes are `Selection.parallelOutcomes` entries the producer already
// consumes; the enumerator (P1a/P1c) wires them into the trace-set.

import type { ProcessModel, ProcessNode, CallNode, AwaitNode, ParallelNode, MergeThreshold, BoolExpr } from "./process-model.js";
import type { NodeId } from "./process-walker-types.js";
import type { Diagnostic } from "./types.js";
import type { DiagnosticCode } from "./diagnostics/codes.js";
import type { TestPin, TestRef } from "./v010-ast.js";
import { estimateProcess, type Injected, type LatencyEnv } from "./process-latency.js";
import type { ParallelOutcome, FailureSpec, FailureOutcome } from "./process-trace-producer.js";
import type { TestPinValue } from "./v010-ast.js";
import { parseDuration } from "@archlang/calc";

// -----------------------------------------------------------------------------
// Pin → IR-node injection map
// -----------------------------------------------------------------------------

function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** A path matches a callee when it equals the callee path or is its trailing
 *  suffix (a bare interface name). */
function pathMatchesCallee(path: readonly string[], callee: readonly string[]): boolean {
  if (path.length === 0) return false;
  if (arraysEqual(path, callee)) return true;
  return path.length < callee.length && arraysEqual(path, callee.slice(callee.length - path.length));
}

/** Does an action node realise a timing pin's step reference? An `await` node
 *  matches a single-segment key against its head text; a `call` node matches a
 *  hop's callee path (a `Caller >`-prefixed key additionally requires the hop's
 *  caller tail to match — the pin's axis comes from its `after`/`takes` word,
 *  not the resolved hop direction). */
function nodeMatchesRef(node: ProcessNode, ref: TestRef): boolean {
  const path = ref.segments.map((s) => s.value);
  if (path.length === 0) return false;
  if (node.kind === "await") {
    return path.length === 1 && (node as AwaitNode).target.text.trim() === path[0];
  }
  if (node.kind === "call") {
    return (node as CallNode).hops.some((h) => {
      if (!pathMatchesCallee(path, h.callee.interfacePath)) return false;
      if (ref.caller && ref.caller.length > 0) {
        return ref.caller[ref.caller.length - 1] === h.caller[h.caller.length - 1];
      }
      return true;
    });
  }
  return false;
}

/**
 * Build the {@link Injected} map from a test's `when` pins: each `after`
 * (business) / `takes` (system) pin resolves to the call/await node it names.
 * `actionNodes` is the emitted-step IR-node list (the choice bridge collects it
 * from the SAME emitter walk), so the keys are the very node objects the fold
 * recurses over.
 */
export function buildInjectionMap(
  actionNodes: readonly ProcessNode[],
  pins: readonly TestPin[],
): Map<ProcessNode, Injected> {
  const map = new Map<ProcessNode, Injected>();
  for (const pin of pins) {
    const v = pin.value;
    if (v.kind !== "after" && v.kind !== "takes") continue;
    const parsed = parseDuration(v.duration.trim());
    if (!parsed.ok) continue;
    const axis = v.kind === "takes" ? "system" : "business";
    for (const node of actionNodes) {
      if (nodeMatchesRef(node, pin.key)) map.set(node, { axis, lo: parsed.lo, hi: parsed.hi });
    }
  }
  return map;
}

// -----------------------------------------------------------------------------
// Pin → failure-injection map (P3)
// -----------------------------------------------------------------------------

/** A single `fails ["cause"]` atom → a fail outcome; any other atom → `ok`. */
function atomToOutcome(v: TestPinValue): FailureOutcome {
  if (v.kind === "fail") return v.cause != null ? { kind: "fail", cause: v.cause } : { kind: "fail" };
  return { kind: "ok" };
}

/** Is a pin value failure-bearing? A bare `fails`, or a per-attempt sequence with
 *  at least one `fails` atom (a pure-answer sequence like `yes,yes,no` is a
 *  loop/branch pin, not a failure injection). */
export function isFailurePin(v: TestPinValue): boolean {
  if (v.kind === "fail") return true;
  return v.kind === "sequence" && v.steps.some((s) => s.kind === "fail");
}

/** The ordered {@link FailureSpec} a failure-bearing pin value carries. */
function failureSpecOf(v: TestPinValue): FailureSpec {
  if (v.kind === "sequence") return v.steps.map(atomToOutcome);
  return [atomToOutcome(v)];
}

/**
 * Build the {@link FailureSpec} map from a test's `fails` / failure-sequence
 * pins (P3): each resolves to the call/await/note IR node it names (the SAME
 * `actionNodes` list the choice bridge collects, matched via {@link nodeMatchesRef}).
 * Returns the matched-node map plus the set of pins that matched NO node, so the
 * enumerator can raise `test.unmatched-fail` for each.
 */
export function buildFailureMap(
  actionNodes: readonly ProcessNode[],
  pins: readonly TestPin[],
): { map: Map<ProcessNode, FailureSpec>; unmatched: TestPin[] } {
  const map = new Map<ProcessNode, FailureSpec>();
  const unmatched: TestPin[] = [];
  for (const pin of pins) {
    if (!isFailurePin(pin.value)) continue;
    const spec = failureSpecOf(pin.value);
    let hit = false;
    for (const node of actionNodes) {
      if (nodeMatchesRef(node, pin.key)) {
        map.set(node, spec);
        hit = true;
      }
    }
    if (!hit) unmatched.push(pin);
  }
  return { map, unmatched };
}

// -----------------------------------------------------------------------------
// Per-branch virtual wall-clock
// -----------------------------------------------------------------------------

/** A branch's cumulative wall-clock: system + business on one timeline (design
 *  §8). `open` ⇒ an unbounded/gap axis — an infinite clock, so it never wins a
 *  finite race. */
interface Clock {
  readonly lo: number;
  readonly hi: number; // Infinity when open
  readonly open: boolean;
}

/** Fold one branch body under the injection map to its wall-clock (both axes on
 *  one timeline — for a general `race` where any axis can order the finish). */
function branchClock(process: ProcessModel, flow: ProcessNode[], env: LatencyEnv, inject: ReadonlyMap<ProcessNode, Injected>): Clock {
  const est = estimateProcess({ ...process, body: flow }, env, { fragment: true, inject });
  const s = est.timed.system;
  const b = est.timed.business;
  const open = s.open || b.open;
  return { lo: s.lo + b.lo, hi: open ? Infinity : s.hi + b.hi, open };
}

/** Fold one branch body to its completion on a SINGLE axis. A `parallel await
 *  <duration>` boundary is a business-axis race (the timer is a business wait),
 *  so the work is judged on its business completion — an OPEN orthogonal (system)
 *  axis, e.g. an unpriced call, does NOT sink a business-winnable branch. */
function branchAxisClock(
  process: ProcessModel,
  flow: ProcessNode[],
  env: LatencyEnv,
  inject: ReadonlyMap<ProcessNode, Injected>,
  axis: "system" | "business",
): Clock {
  const est = estimateProcess({ ...process, body: flow }, env, { fragment: true, inject });
  const b = axis === "business" ? est.timed.business : est.timed.system;
  return { lo: b.lo, hi: b.open ? Infinity : b.hi, open: b.open };
}

/** A fixed-duration clock (the `parallel await` timer). */
function fixedClock(lo: number, hi: number): Clock {
  return { lo, hi, open: false };
}

// -----------------------------------------------------------------------------
// Winner selection
// -----------------------------------------------------------------------------

/** The candidate outcomes for one parallel container: a single entry ⇒ a
 *  deterministic winner; more than one ⇒ nondeterministic (every plausible
 *  winner). */
export interface RaceResolution {
  readonly candidates: Map<NodeId, readonly ParallelOutcome[]>;
  /** Containers with a single deterministic outcome — the enumerator drops these
   *  from its OPEN list. */
  readonly resolved: Set<NodeId>;
}

/** Index order of branch clocks, fastest first (an OPEN clock sorts last). */
function fastestFirst(clocks: readonly Clock[]): number[] {
  return clocks.map((_, i) => i).sort((a, b) => {
    const ca = clocks[a]!;
    const cb = clocks[b]!;
    if (ca.hi !== cb.hi) return ca.hi - cb.hi;
    return ca.lo - cb.lo;
  });
}

/** The n fastest branches deterministically win iff the winner set is strictly
 *  separated from the losers: max(winner.hi) < min(loser.lo). An OPEN winner
 *  (hi = ∞) fails this — genuinely ambiguous. With no losers (n covers every
 *  branch) the separation is vacuous — always deterministic. `n` is pre-clamped
 *  to `[1, order.length]` by the caller. */
function separated(order: readonly number[], clocks: readonly Clock[], n: number): boolean {
  if (n >= order.length) return true;
  const maxWinnerHi = Math.max(...order.slice(0, n).map((i) => clocks[i]!.hi));
  const minLoserLo = Math.min(...order.slice(n).map((i) => clocks[i]!.lo));
  return maxWinnerHi < minLoserLo;
}

/**
 * Resolve every timing-decidable parallel to its race outcome(s). `parallels`
 * maps each container's producer nodeId to its IR node (the bridge collects it).
 * Only `race` / `race N` (form `branches`, which CANCELS its losers) and
 * `parallel await … else` are resolved here; `join`/`join N` (keeps every branch),
 * boolean-merge, fan-out, and event-boundary parallels are left at their
 * structural default (still OPEN).
 */
export function resolveRaces(
  process: ProcessModel,
  parallels: ReadonlyMap<NodeId, ParallelNode>,
  inject: ReadonlyMap<ProcessNode, Injected>,
  env: LatencyEnv,
): RaceResolution {
  const candidates = new Map<NodeId, readonly ParallelOutcome[]>();
  const resolved = new Set<NodeId>();

  for (const [nodeId, node] of parallels) {
    const outcomes = resolveOne(process, node, inject, env);
    if (!outcomes) continue;
    candidates.set(nodeId, outcomes);
    if (outcomes.length === 1) resolved.add(nodeId);
  }
  return { candidates, resolved };
}

function complete(branches: number[]): ParallelOutcome {
  return { kind: "complete", branches };
}

function resolveOne(
  process: ProcessModel,
  node: ParallelNode,
  inject: ReadonlyMap<ProcessNode, Injected>,
  env: LatencyEnv,
): readonly ParallelOutcome[] | undefined {
  // `parallel await d { W } else { E }` — a race between the guarded work and a
  // fixed timer. The work sits in the base branches; the desugared timer/else
  // branch is index base.length (design: producer consumes that index).
  if (node.awaitClause) {
    if (node.body.form !== "branches") return undefined; // fan-out boundary — out of scope
    const d = parseDuration(node.awaitClause.head.text.trim());
    if (!d.ok) return undefined; // event/condition boundary, not a timer
    const workBranches = node.body.branches;
    const timerIdx = workBranches.length;
    const workIndices = workBranches.map((_, i) => i);
    // The `await <duration>` timer is a BUSINESS wait, so the boundary is a
    // business-axis race: judge each work branch on its BUSINESS completion vs the
    // timer. A branch's OPEN orthogonal (system) axis — e.g. an unpriced call —
    // must not sink it (the axes are separate; only the timer's axis orders this
    // race). A genuinely OPEN business wait still loses to the finite timer.
    const workClock = minClock(workBranches.map((b) => branchAxisClock(process, [...b.body], env, inject, "business")));
    const timer = fixedClock(d.lo, d.hi);
    if (workClock.open) return [complete([timerIdx])];          // unbounded business wait → timer fires first
    if (workClock.hi < timer.lo) return [complete(workIndices)]; // work finishes within the window
    if (workClock.lo >= timer.hi) return [complete([timerIdx])]; // timer elapses first
    return [complete(workIndices), complete([timerIdx])];        // overlap — nondeterministic
  }

  if (node.body.form !== "branches") return undefined;
  const threshold = node.merge.threshold;
  // Only `race` / `race N` CANCELS its losers — so it is the sole construct whose
  // trace SHAPE the winner drives. `join N` proceeds at the N-th completion but
  // KEEPS every branch running, so all its steps still appear (the structural
  // default); its threshold only affects timing, not the step set. Boolean-merge
  // / join-all likewise stay at their default.
  if (node.merge.policy !== "race") return undefined;

  const requested = threshold?.kind === "count" ? threshold.n : 1;
  const clocks = node.body.branches.map((b) => branchClock(process, [...b.body], env, inject));
  const order = fastestFirst(clocks);
  const n = Math.max(1, Math.min(requested, order.length));
  const winners = order.slice(0, n).sort((a, b) => a - b);
  if (separated(order, clocks, n)) return [complete(winners)];
  // Nondeterministic: for a first-wins race, every branch is a plausible sole
  // winner; for join/race N with no clean separation, fall back to the
  // structural all-complete outcome so the container stays OPEN without an
  // outcome explosion.
  if (n === 1) return clocks.map((_, i) => complete([i]));
  return [complete(node.body.branches.map((_, i) => i))];
}

// -----------------------------------------------------------------------------
// Explicit per-branch completion + await-interrupt (P5 items 2, 3)
// -----------------------------------------------------------------------------

/** The single-segment key a completion / await-interrupt pin names (a branch
 *  name or an `await` head text); a `Caller >`-prefixed or multi-segment ref is
 *  not a completion key. */
function completionKey(ref: TestRef): string | undefined {
  if (ref.caller && ref.caller.length > 0) return undefined;
  if (ref.segments.length !== 1) return undefined;
  return ref.segments[0]!.value.trim();
}

/** Evaluate a merge threshold over the completed (done) branch-name set. Bare
 *  (no threshold) = AND-join (every branch); `count N` = at least N done; a
 *  boolean = the authored expression over branch names. */
export function evalMerge(threshold: MergeThreshold | undefined, done: ReadonlySet<string>, allNames: readonly string[]): boolean {
  if (!threshold) return allNames.every((n) => done.has(n));
  if (threshold.kind === "count") return done.size >= threshold.n;
  return evalBool(threshold.expr, done);
}

function evalBool(e: BoolExpr, done: ReadonlySet<string>): boolean {
  switch (e.kind) {
    case "name": return done.has(e.name.trim());
    case "and": return e.terms.every((t) => evalBool(t, done));
    case "or": return e.terms.some((t) => evalBool(t, done));
  }
}

/**
 * Resolve the EXPLICIT per-branch completion pins (design §5 item 2) and the
 * `elapses`/`within` await-interrupt pins (item 3) into parallel outcomes,
 * OVERRIDING the timing-derived `resolveRaces` result for the same container.
 *
 * Per-branch completion (`fraud: done` / `fails` / `never` / `after` / `takes`,
 * keyed by branch name): `done`/`after`/`takes` ⇒ the branch completes (in the
 * done-set + its steps appear); `fails` ⇒ its steps appear but it is NOT done
 * (fails the merge); `never` ⇒ its steps are elided and it is not done. The
 * merge threshold (`join N` / boolean / bare AND-join) is then evaluated over
 * the done-set: unsatisfied ⇒ the outcome carries `mergeFail` so the producer
 * fails the parallel.
 *
 * Await interrupt (`<head>: elapses | within`, keyed by the `parallel await`
 * head): `elapses` ⇒ the timer branch wins (the `else` path); `within` ⇒ the
 * guarded work wins.
 */
export function resolveCompletions(
  parallels: ReadonlyMap<NodeId, ParallelNode>,
  pins: readonly TestPin[],
  diagnostics: Diagnostic[] = [],
): Map<NodeId, ParallelOutcome> {
  const byKey = new Map<string, TestPinValue>();
  const srcByKey = new Map<string, TestPin["source"]>();
  for (const p of pins) {
    const k = completionKey(p.key);
    if (k !== undefined) { byKey.set(k, p.value); srcByKey.set(k, p.source); }
  }

  // Every leading-multi-await trigger head across the process — so a `start:`
  // whose string matches none can be flagged (never a silent no-op).
  const allTriggerHeads = new Set<string>();

  const out = new Map<NodeId, ParallelOutcome>();
  for (const [nodeId, node] of parallels) {
    // Item 4 — a leading MULTI-await desugars (demoteMultiAwaitHead) to a
    // synthesized `race` over single-await branches. Which trigger fired is pinned
    // by the grammar-consistent trigger-TEXT key with an affirmative answer
    // (`"manual kick": yes`), selecting that branch deterministically.
    if (isMultiAwaitRace(node)) {
      const brs = node.body.form === "branches" ? node.body.branches : [];
      const heads = brs.map((b) => (b.body[0] as AwaitNode).target.text.trim());
      for (const h of heads) allTriggerHeads.add(h);
      // `start: "<trigger text>"` — the explicit boundary form: the winner is the
      // branch whose await head text equals the string.
      const startV = byKey.get("start");
      if (startV?.kind === "cause") {
        const idx = heads.findIndex((h) => h === startV.value.trim());
        if (idx >= 0) { out.set(nodeId, complete([idx])); continue; }
      }
      const firedIdx = heads.flatMap((h, i) => {
        const v = byKey.get(h);
        return v?.kind === "answer" && v.word !== "no" && v.word !== "never" ? [i] : [];
      });
      if (firedIdx.length > 1) {
        // Two triggers can't both be the winner — a contradictory test, never
        // silently first-wins.
        diagnostics.push(diag("error", "Multiple leading triggers pinned as fired — a multi-await race has one winner.", "test.ambiguous-trigger", srcByKey.get(heads[firedIdx[0]!]!)));
      }
      if (firedIdx.length > 0) { out.set(nodeId, complete([firedIdx[0]!])); continue; }
    }

    // Item 3 — `parallel await <head> { W } else { E }` interrupt, keyed by the
    // head text OR the boundary's `as` alias (`grace: elapses`).
    if (node.awaitClause) {
      const alias = node.awaitClause.asName?.text?.trim();
      const headV = byKey.get(node.awaitClause.head.text.trim());
      const aliasV = alias ? byKey.get(alias) : undefined;
      if (headV && aliasV && !samePin(headV, aliasV)) {
        diagnostics.push(diag("warning", "Await boundary pinned by BOTH its head and its `as` alias with conflicting values — the head wins.", "test.conflicting-await-pin", srcByKey.get(node.awaitClause.head.text.trim())));
      }
      const v = headV ?? aliasV;
      if (v?.kind === "answer" && (v.word === "elapses" || v.word === "within")) {
        const wb = node.body.form === "branches" ? node.body.branches.length : 0;
        out.set(nodeId, v.word === "elapses"
          ? complete([wb])                                       // timer (else) branch wins
          : complete(Array.from({ length: wb }, (_, i) => i)));  // guarded work wins
        continue;
      }
    }

    // Item 2 — per-branch completion driving the merge. A `race` cancels its
    // losers and picks a winner by timing (or `start`), NOT by a done-set merge —
    // leave it to `resolveRaces`, else a completion pin would run every branch.
    if (node.merge.policy === "race") continue;
    const branches = node.body.form === "branches" || node.body.form === "select" ? node.body.branches : [];
    if (branches.length === 0) continue;
    const named = branches.map((b, i) => ({ name: b.name?.name?.trim(), i }));
    const pinned = named.filter((b) => b.name !== undefined && byKey.has(b.name!));
    if (pinned.length === 0) continue; // no completion pin here — leave to resolveRaces / default

    const done = new Set<string>();
    const appears: number[] = [];
    for (const { name, i } of named) {
      const v = name !== undefined ? byKey.get(name) : undefined;
      if (v === undefined) {
        // Unpinned branch in a partially-pinned parallel: assume it completes
        // (the pins constrain the branches under test; the rest run normally).
        done.add(name ?? `#${i}`);
        appears.push(i);
        continue;
      }
      const kind = completionOutcome(v);
      if (kind === "done") { done.add(name!); appears.push(i); }
      else if (kind === "fails") { appears.push(i); }       // steps appear, not done
      // `never` — elided, not done
    }
    const allNames = named.map((b) => b.name ?? `#${b.i}`);
    const satisfied = evalMerge(node.merge.threshold, done, allNames);
    out.set(nodeId, satisfied
      ? complete(appears.sort((a, b) => a - b))
      : { kind: "complete", branches: appears.sort((a, b) => a - b), mergeFail: {} });
  }

  // A `start: "<trigger>"` that named no leading-await head (a typo, or a process
  // with no multi-await) is an author error — flag it, never a silent no-op.
  const startV = byKey.get("start");
  if (startV?.kind === "cause" && !allTriggerHeads.has(startV.value.trim())) {
    diagnostics.push(diag("error", `\`start\` names no leading await head '${startV.value}' — the process has no such trigger.`, "test.unmatched-trigger", srcByKey.get("start")));
  }
  return out;
}

function diag(severity: "error" | "warning", message: string, code: DiagnosticCode, source?: TestPin["source"]): Diagnostic {
  return { severity, message, code, ...(source ? { source } : {}) };
}

/** Structural equality of two pin values for the conflict check (only the forms a
 *  completion/await-interrupt pin takes need comparing). */
function samePin(a: TestPinValue, b: TestPinValue): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "answer" && b.kind === "answer") return a.word === b.word;
  if (a.kind === "after" && b.kind === "after") return a.duration === b.duration;
  if (a.kind === "takes" && b.kind === "takes") return a.duration === b.duration;
  return true; // same kind, no distinguishing scalar we care about here
}

/** Is this the synthesized race a leading MULTI-await head demotes to (§2.5):
 *  a race over ≥2 unnamed single-`await` branches, no boundary clause? (A
 *  user-written `parallel race` normally names its branches or holds real work.) */
function isMultiAwaitRace(node: ParallelNode): boolean {
  if (node.awaitClause || node.merge.policy !== "race") return false;
  if (node.body.form !== "branches" || node.body.branches.length < 2) return false;
  return node.body.branches.every((b) => !b.name && b.body.length === 1 && b.body[0]?.kind === "await");
}

/** Classify a completion pin value: `done`/`after`/`takes` ⇒ the branch
 *  completes; `fails` ⇒ ran but failed; `never` ⇒ absent. */
function completionOutcome(v: TestPinValue): "done" | "fails" | "never" {
  if (v.kind === "after" || v.kind === "takes") return "done";
  if (v.kind === "fail") return "fails";
  if (v.kind === "answer") {
    if (v.word === "never") return "never";
    if (v.word === "fails") return "fails";
  }
  return "done"; // `done`, `ok`, `pass`, or any other affirmative
}

/** Earliest completion across branches (min wall-clock); OPEN only if ALL open. */
function minClock(clocks: readonly Clock[]): Clock {
  if (clocks.length === 0) return fixedClock(0, 0);
  const finite = clocks.filter((c) => !c.open);
  if (finite.length === 0) return { lo: Math.min(...clocks.map((c) => c.lo)), hi: Infinity, open: true };
  return {
    lo: Math.min(...clocks.map((c) => c.lo)),
    hi: Math.min(...finite.map((c) => c.hi)),
    open: false,
  };
}
